// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared utility functions for transforming Digital Twin API entities
 * into display-friendly formats for the DCAssets and KnowledgeGraph pages.
 */

import type { ValueEnvelope, EntityState, Entity, Relationship } from '../types/digitalTwin';
import { EntitySchemas, MeasurementGroups } from '../config/entity-schemas';
import { SHOW_ALL_ENTITY_TYPES } from '../config/featureFlags';

// ---------------------------------------------------------------------------
// Entity type helpers
// ---------------------------------------------------------------------------

if (SHOW_ALL_ENTITY_TYPES && process.env.NODE_ENV === 'development') {
  console.warn(
    '[Helios] REACT_APP_SHOW_ALL_ENTITY_TYPES is ON — all entity types will be shown. ' +
      'Set to false or remove from .env.local to restore filtering.'
  );
}

/**
 * `brick:Temperature_Setpoint` → `Temperature_Setpoint`.
 *
 * GA-10 publishes 1318 entities under prefixed Brick vocabularies (`brick:`,
 * `wsch_brick:`); MOCK-GA10 publishes the same concepts bare. Every type
 * lookup strips the prefix first so the two sites resolve alike —
 * `cleanEntityType` does it, so the label, icon and code lookups built on it
 * inherit it.
 */
export const stripOntologyPrefix = (raw: string): string => raw.replace(/^[A-Za-z_][\w.-]*:/, '');

/** Strip the prefix and the "Type" suffix: "brick:PDUType" -> "PDU", "GeneratorType" -> "Generator" */
export const cleanEntityType = (raw: string): string =>
  stripOntologyPrefix(raw).replace(/Type$/, '');

/**
 * Physical equipment and infrastructure entity types from the ontology schema.
 * Used to filter KG/Assets pages — telemetry points, documents, and abstract
 * types are excluded so only real assets appear in the hierarchy and graph.
 *
 * Source: definitions.Entity.oneOf in data-model-schema.json, curated to
 * include only equipment, rooms, zones, and infrastructure.
 */
export const ASSET_ENTITY_TYPES: ReadonlySet<string> = new Set([
  // Electrical infrastructure
  'ATSType',
  'BuswayType',
  'FeederType',
  'GroundingNetworkType',
  'MCCType',
  'MotorFeederType',
  'PDUType',
  'PQMeterType',
  'PowerDeviceType',
  'PanelboardType',
  'RPPType',
  'RackPDUType',
  'SPDType',
  'STSType',
  'SwitchgearType',
  'TransformerType',
  'UPSType',
  'UtilityMeterType',
  // Generation and storage
  'BESSType',
  'GeneratorSetType',
  // HVAC and cooling
  'AHU',
  'Air_Cooled_Chiller',
  'Air_Handling_Unit',
  'Air_Header',
  'CRAH',
  'Chilled_Water_Loop',
  'Chilled_Water_Pump',
  'Chilled_Water_Thermal_Energy_Storage_Tank',
  'Chiller',
  'Chiller_Plant',
  'Chiller_Plant_Header',
  'Condenser_Water_Pump',
  'Condensing_Unit',
  'Cooling_Coil',
  'Cooling_Tower',
  'Cooling_Tower_Fan',
  'Economizer',
  'Evaporator',
  'Fan',
  'Fan_Coil_Unit',
  'HVAC_Equipment',
  'Outside_Damper',
  'Rooftop_Unit',
  'Supply_Fan',
  // Rooms, zones, and physical spaces
  'BatteryRoom',
  'Cold_Aisle',
  'DataHall',
  'ElectricalRoom',
  'GeneratorRoom',
  'GeneratorYard',
  'HVACZone',
  'Hot_Aisle',
  'Mechanical_Room',
  // IT infrastructure
  'ITRackType',
  'IT_Rack',
  // Site and interconnection
  'SiteType',
  'PointOfInterconnection',
  // Control and monitoring equipment
  'BMSType',
  'ControllerType',
  'DeviceType',
  'GatewayType',
]);

/**
 * Return true if the entity type represents a physical asset or piece of
 * equipment that should appear on the Knowledge Graph and Assets pages.
 * Telemetry points, documents, metrics, and abstract types return false.
 */
export const isAssetEntityType = (rawType: string): boolean => {
  if (SHOW_ALL_ENTITY_TYPES) return true;
  const bare = stripOntologyPrefix(rawType);
  return ASSET_ENTITY_TYPES.has(bare) || ASSET_ENTITY_TYPES.has(bare + 'Type');
};

/**
 * Return the ontology display label for an entity type.
 * Uses the auto-generated EntitySchemas map (e.g. "GeneratorSetType" → "Generator Set"),
 * falling back to `cleanEntityType` when the type is not in the schema.
 *
 * Only 29 of the ontology's 102 entity types carry a schema label, and the rest
 * are Brick-style names (`Air_Cooled_Chiller`, `Chilled_Water_Pump`), so the
 * fallback also swaps underscores for spaces — otherwise every HVAC asset shows
 * a raw identifier in the UI.
 */
export const getEntityDisplayType = (rawType: string): string => {
  const bare = stripOntologyPrefix(rawType);
  return EntitySchemas[bare]?.label ?? cleanEntityType(bare).replace(/_/g, ' ');
};

const ENTITY_ICONS: Record<string, string> = {
  Site: '🏢',
  UtilityFeeder: '🏭',
  BESS: '🔋',
  GeneratorSet: '⚙️',
  Generator: '⚙️',
  MainSwitchboard: '⚡',
  UPS: '⚡',
  PDU: '🔌',
  Rack: '🖥️',
  ITRack: '🖥️',
  RackPDU: '🔌',
  Chiller: '🧊',
  Air_Cooled_Chiller: '🧊',
  CRAC: '❄️',
  CRAH: '❄️',
  AHU: '🌬️',
  Air_Header: '🌬️',
  Panelboard: '⚡',
  Transformer: '🔺',
  ATS: '🔀',
  STS: '🔀',
  Switchgear: '⚡',
  SwitchGear: '⚡',
  Feeder: '🔗',
  MotorFeeder: '🔗',
  Busway: '➡️',
  RPP: '🔌',
  Controller: '🎛️',
  Gateway: '📡',
  BMS: '🔋',
  PQMeter: '📊',
  UtilityMeter: '📊',
  SPD: '🛡️',
  GroundingNetwork: '⏚',
  MCC: '🏗️',
  PowerDevice: '⚡',
  Device: '📦',
};

export const getEntityIcon = (entityType: string): string => {
  const clean = cleanEntityType(entityType);
  return ENTITY_ICONS[clean] || '📦';
};

/**
 * Short (≤5 char) uppercase code used for the monospace icon chip in the
 * asset tree/detail views (e.g. "CRAH", "PDU", "XFMR"). Common types get a
 * curated legible code; anything else falls back to an algorithmic derivation
 * from the cleaned type name. Keyed on the "Type"-stripped name.
 */
const ENTITY_CODES: Record<string, string> = {
  Site: 'SITE',
  UtilityFeeder: 'UFDR',
  UtilityMeter: 'UMTR',
  PointOfInterconnection: 'POI',
  BESS: 'BESS',
  GeneratorSet: 'GEN',
  Generator: 'GEN',
  GeneratorRoom: 'GENR',
  GeneratorYard: 'GENY',
  MainSwitchboard: 'MSB',
  Switchgear: 'SWGR',
  SwitchGear: 'SWGR',
  UPS: 'UPS',
  PDU: 'PDU',
  RackPDU: 'RPDU',
  ITRack: 'RACK',
  IT_Rack: 'RACK',
  Rack: 'RACK',
  Chiller: 'CHLR',
  Air_Cooled_Chiller: 'ACCH',
  Chiller_Plant: 'CPLT',
  Chiller_Plant_Header: 'CPHD',
  Chilled_Water_Pump: 'CWP',
  Condenser_Water_Pump: 'CDWP',
  Cooling_Tower: 'CT',
  Cooling_Tower_Fan: 'CTF',
  CRAC: 'CRAC',
  CRAH: 'CRAH',
  AHU: 'AHU',
  Air_Header: 'AHDR',
  Economizer: 'ECON',
  Outside_Damper: 'DMPR',
  Fan: 'FAN',
  Panelboard: 'PNL',
  Transformer: 'XFMR',
  ATS: 'ATS',
  STS: 'STS',
  Feeder: 'FDR',
  MotorFeeder: 'MFDR',
  Busway: 'BUS',
  RPP: 'RPP',
  Controller: 'CTRL',
  Gateway: 'GW',
  BMS: 'BMS',
  PQMeter: 'PQM',
  SPD: 'SPD',
  MCC: 'MCC',
  GroundingNetwork: 'GND',
  PowerDevice: 'PWR',
  Device: 'DEV',
  BatteryRoom: 'BATT',
  ElectricalRoom: 'ELEC',
  DataHall: 'HALL',
  Cold_Aisle: 'CA',
  Hot_Aisle: 'HA',
  HVACZone: 'HVAC',
};

/** Algorithmic fallback: derive a short uppercase code from a cleaned type. */
const deriveEntityCode = (clean: string): string => {
  const words = clean
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) {
    const w = words[0];
    return (w.length <= 5 ? w : w.slice(0, 4)).toUpperCase();
  }
  return words
    .map(w => w[0])
    .join('')
    .slice(0, 5)
    .toUpperCase();
};

/**
 * Return a short uppercase code (≤5 chars) for an entity type, used for the
 * monospace icon chip in the redesigned Assets views.
 */
export const getEntityCode = (entityType: string): string => {
  const clean = cleanEntityType(entityType);
  return ENTITY_CODES[clean] ?? deriveEntityCode(clean);
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

export type StatusCategory = 'healthy' | 'warning' | 'alarming' | 'offline' | 'unknown';

export const categorizeStatus = (status: string | undefined): StatusCategory => {
  if (!status) return 'unknown';
  const lower = status.toLowerCase();
  if (['operational', 'ok', 'online', 'healthy', 'active'].includes(lower)) return 'healthy';
  if (['warning', 'maintenance', 'degraded'].includes(lower)) return 'warning';
  if (['alarming', 'fault', 'error', 'critical'].includes(lower)) return 'alarming';
  if (['offline', 'down', 'inactive'].includes(lower)) return 'offline';
  return 'unknown';
};

export const getStatusCssClass = (status: StatusCategory): string => {
  switch (status) {
    case 'healthy':
      return 'status-healthy';
    case 'warning':
      return 'status-warning';
    case 'alarming':
      return 'status-alarming';
    case 'offline':
      return 'status-offline';
    default:
      return '';
  }
};

export const STATUS_COLORS: Record<StatusCategory, string> = {
  healthy: '#27AE60',
  warning: '#F39C12',
  alarming: '#E74C3C',
  offline: '#95A5A6',
  unknown: '#95A5A6',
};

/** The four visual status states used by the redesigned Assets views. */
export type StatusToken = 'ok' | 'warn' | 'fault' | 'idle';

/**
 * Map a StatusCategory to one of the four mock status tokens that drive the
 * `dca-st-*` CSS classes (row left-border + pill color). Offline and unknown
 * both collapse to the neutral "idle" state.
 */
export const getStatusToken = (status: StatusCategory): StatusToken => {
  switch (status) {
    case 'healthy':
      return 'ok';
    case 'warning':
      return 'warn';
    case 'alarming':
      return 'fault';
    default:
      return 'idle';
  }
};

// ---------------------------------------------------------------------------
// Measurement helpers
// ---------------------------------------------------------------------------

export interface MeasurementEntry {
  key: string;
  rawKey: string;
  display: string;
}

export interface GroupedMeasurements {
  group: string;
  entries: MeasurementEntry[];
}

/** Format a ValueEnvelope for display. Returns null when nothing to show. */
export const formatMeasurement = (envelope: ValueEnvelope | undefined): string | null => {
  if (!envelope || envelope.value == null) return null;
  const val =
    typeof envelope.value === 'number' ? envelope.value.toLocaleString() : String(envelope.value);
  return envelope.unit ? `${val} ${envelope.unit}` : val;
};

/** "ActivePower" -> "Active Power", "DCVoltage" -> "DC Voltage" */
const humanizeKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^ /, '')
    .replace(/D C /g, 'DC ')
    .trim();
};

/** Extract all displayable measurements from entity state, excluding 'status' and 'updatedAt'. */
export const extractMeasurements = (state: EntityState | undefined): MeasurementEntry[] => {
  if (!state) return [];
  const results: MeasurementEntry[] = [];
  for (const [key, value] of Object.entries(state)) {
    if (key === 'status' || key === 'updatedAt') continue;
    if (value && typeof value === 'object' && 'value' in value) {
      const formatted = formatMeasurement(value as ValueEnvelope);
      if (formatted) {
        results.push({ key: humanizeKey(key), rawKey: key, display: formatted });
      }
    }
  }
  return results;
};

/** Group measurements by ontology MeasurementGroup category. */
export const groupMeasurements = (state: EntityState | undefined): GroupedMeasurements[] => {
  if (!state) return [];
  const measurements = extractMeasurements(state);
  if (measurements.length === 0) return [];

  // Build lookup: raw field name → group label
  const fieldToGroup = new Map<string, string>();
  for (const g of MeasurementGroups) {
    for (const f of g.fields) fieldToGroup.set(f, g.label);
  }

  const groups = new Map<string, MeasurementEntry[]>();
  for (const m of measurements) {
    const groupLabel = fieldToGroup.get(m.rawKey) ?? 'Other';
    if (!groups.has(groupLabel)) groups.set(groupLabel, []);
    groups.get(groupLabel)!.push(m);
  }

  return Array.from(groups.entries())
    .map(([group, entries]) => ({ group, entries }))
    .sort((a, b) => a.group.localeCompare(b.group));
};

// ---------------------------------------------------------------------------
// Relationship / dependency helpers
// ---------------------------------------------------------------------------

export const getEntityInstanceLabel = (entityId: string, entities: Entity[]): string => {
  const entity = entities.find(e => e.id === entityId);
  return entity?.attributes?.label ?? entityId;
};

/** For a given entity, find what it feeds (outgoing) and what feeds it (incoming). */
export const getEntityDependencies = (
  entityId: string,
  relationships: Relationship[],
  entities: Entity[]
): { feeds: string[]; fedBy: string[] } => {
  const feeds = relationships
    .filter(r => r.source === entityId)
    .map(r => getEntityInstanceLabel(r.target, entities));
  const fedBy = relationships
    .filter(r => r.target === entityId)
    .map(r => getEntityInstanceLabel(r.source, entities));
  return { feeds, fedBy };
};

// ---------------------------------------------------------------------------
// Entity status extraction
// ---------------------------------------------------------------------------

/** Safely extract the status string from an entity's state. */
export const getEntityStatus = (entity: Entity): string | undefined => {
  const statusEnvelope = entity.state?.status;
  if (statusEnvelope && typeof statusEnvelope === 'object' && 'value' in statusEnvelope) {
    const val = (statusEnvelope as ValueEnvelope).value;
    return typeof val === 'string' ? val : undefined;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Attribute formatting
// ---------------------------------------------------------------------------

/**
 * Format an entity attribute value for display.
 * Handles {value, unit} objects (ValueEnvelope-like), primitives, and arrays.
 */
export const formatAttributeValue = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val === 'object' && !Array.isArray(val)) {
    const obj = val as Record<string, unknown>;
    if ('value' in obj) {
      if (obj.value == null) return '';
      const v = typeof obj.value === 'number' ? obj.value.toLocaleString() : String(obj.value);
      const unit = obj.unit != null ? String(obj.unit) : '';
      return unit.trim() ? `${v} ${unit}` : v;
    }
    return JSON.stringify(val);
  }
  return String(val);
};

// ---------------------------------------------------------------------------
// Child entity aggregation (decomposed telemetry model)
// ---------------------------------------------------------------------------

/**
 * Relationship types that link a parent asset to a child telemetry entity,
 * stored parent → child.
 *
 * `hasTelemetryPoint` and `hasTelemetryStream` are from the ontology
 * (`definitions.Relationship.oneOf`); `hasPoint` is not, and is tolerated only
 * because earlier mock fixtures used it.
 */
const TELEMETRY_RELATIONSHIP_TYPES = new Set([
  'hasTelemetryPoint',
  'hasTelemetryStream',
  'hasPoint',
  // GA-10 hangs 885 setpoints and limits off its assets with this; MOCK-GA10
  // funnels the same points through `hasTelemetryPoint`. Same relationship.
  'hasParameter',
]);

/**
 * Ontology inverses of the above, stored child → parent. Treating these as if
 * they pointed parent → child would register the *asset* as the telemetry
 * point and hide it from the Assets tree and Knowledge Graph.
 */
const INVERSE_TELEMETRY_RELATIONSHIP_TYPES = new Set([
  'isTelemetryOf',
  'isTelemetryStreamOf',
  'isParameterOf',
]);

/**
 * Reduce a relationship to a parent/child telemetry link regardless of which
 * direction the ontology stored it in. Returns null for non-telemetry types.
 */
const telemetryLink = (rel: Relationship): { parentId: string; childId: string } | null => {
  if (TELEMETRY_RELATIONSHIP_TYPES.has(rel.type)) {
    return { parentId: rel.source, childId: rel.target };
  }
  if (INVERSE_TELEMETRY_RELATIONSHIP_TYPES.has(rel.type)) {
    return { parentId: rel.target, childId: rel.source };
  }
  return null;
};

/**
 * Parent entity id → child telemetry entity ids.
 *
 * `getChildTelemetryIds` scans the whole relationship array per call, so the
 * aggregation helpers below degrade to O(entities × relationships) when called
 * in a loop. Build this once and pass it in.
 */
export const buildTelemetryChildIndex = (relationships: Relationship[]): Map<string, string[]> => {
  const index = new Map<string, string[]>();
  for (const rel of relationships) {
    const link = telemetryLink(rel);
    if (!link) continue;
    const existing = index.get(link.parentId);
    if (existing) existing.push(link.childId);
    else index.set(link.parentId, [link.childId]);
  }
  return index;
};

// ---------------------------------------------------------------------------
// Telemetry recency / liveness
// ---------------------------------------------------------------------------

/** An asset is considered "live" if it has telemetry within this window. */
export const TELEMETRY_LIVE_WINDOW_MS = 5 * 60 * 1000;

/** Parse an ISO timestamp to epoch ms, or NaN if missing/invalid. */
const parseTimestamp = (value: unknown): number => {
  if (typeof value !== 'string') return NaN;
  return new Date(value).getTime();
};

/**
 * Build a map of entity id → latest telemetry timestamp (epoch ms). Considers
 * each entity's own `state.updatedAt` plus the timestamps of any child
 * telemetry-point entities linked via hasTelemetryPoint/hasPoint (the value is
 * the most recent of the two). Entities with no usable timestamp are omitted.
 * Single pass over entities + relationships.
 */
export const buildTelemetryRecencyMap = (
  entities: Entity[],
  relationships: Relationship[]
): Map<string, number> => {
  // First, each entity's own timestamp.
  const own = new Map<string, number>();
  for (const e of entities) {
    const t = parseTimestamp(e.state?.updatedAt);
    if (!Number.isNaN(t)) own.set(e.id, t);
  }

  // Result starts from own timestamps, then absorbs child telemetry-point times.
  const result = new Map<string, number>(own);
  for (const r of relationships) {
    const link = telemetryLink(r);
    if (!link) continue;
    const childT = own.get(link.childId);
    if (childT == null) continue;
    const current = result.get(link.parentId);
    if (current == null || childT > current) result.set(link.parentId, childT);
  }

  return result;
};

/**
 * True when `lastMs` is a real timestamp within `windowMs` before `now`.
 * Timestamps in the future (clock skew or bad telemetry) are NOT treated as
 * live — the delta must be non-negative and within the window.
 */
export const isTelemetryLive = (
  lastMs: number | undefined,
  now: number,
  windowMs: number = TELEMETRY_LIVE_WINDOW_MS
): boolean => {
  if (lastMs == null || Number.isNaN(lastMs)) return false;
  const delta = now - lastMs;
  return delta >= 0 && delta <= windowMs;
};

/**
 * Find child telemetry entities linked to a parent and return their IDs.
 * Handles both stored directions (hasTelemetryPoint/Stream and their inverses).
 *
 * O(relationships) per call — pass a `buildTelemetryChildIndex` result when
 * calling this for many parents.
 */
export const getChildTelemetryIds = (
  parentId: string,
  relationships: Relationship[],
  childIndex?: ReadonlyMap<string, string[]>
): string[] => {
  if (childIndex) return childIndex.get(parentId) ?? [];
  const ids: string[] = [];
  for (const r of relationships) {
    const link = telemetryLink(r);
    if (link && link.parentId === parentId) ids.push(link.childId);
  }
  return ids;
};

/**
 * Return the IDs of every entity that is a telemetry point — i.e. the
 * target of a hasTelemetryPoint/hasPoint relationship from any parent.
 * This is a structural check derived from the graph itself, so it stays
 * correct even if an entity's `type` isn't (yet) covered by the
 * ASSET_ENTITY_TYPES allow-list above.
 */
export const getTelemetryEntityIds = (relationships: Relationship[]): Set<string> => {
  const ids = new Set<string>();
  for (const rel of relationships) {
    const link = telemetryLink(rel);
    if (link) ids.add(link.childId);
  }
  return ids;
};

/**
 * Aggregate telemetry measurements from child entities linked via
 * hasTelemetryPoint/hasPoint relationships. Returns a synthetic EntityState
 * that merges the parent's own state with state from child entities.
 */
export const aggregateTelemetryState = (
  entity: Entity,
  allEntities: Entity[],
  relationships: Relationship[],
  prebuiltMap?: Map<string, Entity>,
  childIndex?: ReadonlyMap<string, string[]>
): EntityState | undefined => {
  // Start with the parent's own state entries
  const merged: EntityState = { ...(entity.state ?? {}) };
  let hasAny = entity.state ? Object.keys(entity.state).length > 1 : false; // >1 to skip updatedAt
  let latestUpdatedAt: string | undefined = (entity.state?.updatedAt as string) || undefined;

  const childIds = getChildTelemetryIds(entity.id, relationships, childIndex);
  if (childIds.length === 0) return entity.state;

  const entityMap = prebuiltMap ?? new Map(allEntities.map(e => [e.id, e]));

  for (const childId of childIds) {
    const child = entityMap.get(childId);
    if (!child?.state) continue;

    // Track the most recent updatedAt across children (compare as epoch ms)
    const childUpdated = child.state.updatedAt as string | undefined;
    if (
      childUpdated &&
      (!latestUpdatedAt || new Date(childUpdated).getTime() > new Date(latestUpdatedAt).getTime())
    ) {
      latestUpdatedAt = childUpdated;
    }

    for (const [key, value] of Object.entries(child.state)) {
      if (key === 'updatedAt' || key === 'status') continue;
      if (value && typeof value === 'object' && 'value' in value) {
        // Use the child's entity type as the key (e.g. "SoC", "ActivePower")
        const displayKey = child.type;
        if (!merged[displayKey]) {
          merged[displayKey] = value as ValueEnvelope;
          hasAny = true;
        }
      }
    }
  }

  if (latestUpdatedAt) {
    merged.updatedAt = latestUpdatedAt;
  }

  return hasAny ? merged : entity.state;
};

/**
 * Aggregate status from child telemetry entities when the parent has no
 * inline status. Returns the worst status found among children, or undefined.
 */
export const aggregateChildStatus = (
  entity: Entity,
  allEntities: Entity[],
  relationships: Relationship[],
  prebuiltMap?: Map<string, Entity>,
  childIndex?: ReadonlyMap<string, string[]>
): string | undefined => {
  // If parent already has a status, use it
  const parentStatus = getEntityStatus(entity);
  if (parentStatus) return parentStatus;

  const childIds = getChildTelemetryIds(entity.id, relationships, childIndex);
  if (childIds.length === 0) return undefined;

  const entityMap = prebuiltMap ?? new Map(allEntities.map(e => [e.id, e]));
  const statuses: string[] = [];

  for (const childId of childIds) {
    const child = entityMap.get(childId);
    if (!child) continue;
    const s = getEntityStatus(child);
    if (s) statuses.push(s);
  }

  if (statuses.length === 0) return undefined;

  // Return worst status: alarming > warning > offline > unknown > healthy
  const priority: Record<string, number> = {};
  for (const s of statuses) {
    const cat = categorizeStatus(s);
    const score =
      cat === 'alarming'
        ? 4
        : cat === 'warning'
          ? 3
          : cat === 'offline'
            ? 2
            : cat === 'unknown'
              ? 1
              : 0;
    priority[s] = score;
  }
  return statuses.sort((a, b) => (priority[b] ?? 0) - (priority[a] ?? 0))[0];
};
